import { APIResponse } from '@src/types/common/api'
import * as ts from '@src/types/agent'
import { request } from './request'

export async function createAgent(data: Omit<ts.Agent, 'id'>): Promise<APIResponse<ts.Agent>> {
  return request.post('/agent', data)
}
export async function updateAgent(data: Partial<ts.Agent>): Promise<APIResponse<void>> {
  return request.put('/agent', data)
}
export async function delAgent(id: number): Promise<APIResponse<void>> {
  return request.delete(`/agent/${id}`)
}
export async function getAgentInfo(id: number): Promise<APIResponse<ts.AgentInfo>> {
  return request.get(`/agent/detail/${id}`)
}
export async function getMyAgents(): Promise<APIResponse<ts.AgentItem[]>> {
  return request.get('/agent')
}
export async function getAllAgents(params: {
  page?: number
  name?: string
}): Promise<APIResponse<{ entities: ts.AgentItem[]; total: number }>> {
  return request.get('/agent/page', { params })
}

export async function getStyleAdjectives(): Promise<
  APIResponse<{ style: string[]; adjectives: string[] }>
> {
  return request.get('/agent/style-adjectives')
}

export async function cloneTwitterAgent(
  data: ts.CloneTwitterAgentParams,
): Promise<APIResponse<ts.AgentInfo>> {
  return request.post('/twitter/generate-character', data)
}
export async function getKOLs(params: {
  page?: number
  agentId: number
}): Promise<APIResponse<{ total: number; data: ts.Kol[] }>> {
  return request.get('/agent-kol', { params })
}

export async function addKOL(data: { kols: string[]; agentId: number }): Promise<APIResponse> {
  return request.post('/agent-kol', data)
}

export async function updateKOL(kol: ts.EditKol): Promise<APIResponse> {
  return request.put('/agent-kol', kol)
}
export async function deleteKOL(id: number): Promise<APIResponse> {
  return request.delete(`/agent-kol/${id}`)
}

export async function getAgentRule(agentId: number): Promise<APIResponse<ts.TwitterRule>> {
  return request.get(`/twitter/config/${agentId}`)
}
export async function editTwitterRule(data: ts.EditTwitterRule): Promise<APIResponse> {
  return request.post('/twitter/edit-rule', data)
}

export async function createManualPostRule(
  data: ts.ManualRuleCreate,
): Promise<APIResponse<{ id: number }>> {
  return request.post(`/manual-post/rule`, data)
}
export async function getManualPostRule(agentId: number): Promise<APIResponse<ts.ManualRule>> {
  return request.get(`manual-post/rule-by-agent-id`, { params: { agentId } })
}
export async function editManualPostRule(data: ts.ManualRuleUpdate): Promise<APIResponse> {
  return request.put(`/manual-post/rule`, data)
}
export async function unbindTelegram(agentId: number): Promise<APIResponse> {
  return request.put(`/manual-post/unbind-telegram`, { agentId })
}

export async function sandboxPost(params: {
  agentId: number
  language: string
}): Promise<APIResponse<ts.SandboxTweetPayload>> {
  return request.post(`/agent/${params.agentId}/sandbox/post`, { language: params.language })
}
export async function getScenarioPost(params: {
  agentId: number
  topic: string
  language: string
}): Promise<APIResponse<ts.SandboxScenarioPost[]>> {
  return request.post(`/agent/${params.agentId}/sandbox/project-summary`, {
    topic: params.topic,
    language: params.language,
  })
}

export async function deductCredit(
  agentId: number,
  type = 'SANDBOX_GENERATE',
): Promise<APIResponse<void>> {
  return request.post(`/agent/${agentId}/sandbox/deduct-credit`, { type })
}

export async function sandboxSelfReply({
  agentId,
  ...data
}: ts.SandboxSelfReply): Promise<APIResponse<{ reply: string }>> {
  return request.post(`/agent/${agentId}/sandbox/self-reply`, data)
}

export async function getScheduleInfo(agentId: number): Promise<APIResponse<ts.ScheduleInfo>> {
  return request.get(`/agent/${agentId}/schedule-info`)
}

export async function generatePresignedUrl(
  fileType: string,
): Promise<APIResponse<{ presignedUrl: string; fileUrl: string }>> {
  return request.post('/agent/generate-presigned-url', { fileType })
}

export async function postNow(agentId: number): Promise<APIResponse<void>> {
  return request.post(`/agent/${agentId}/post/now`)
}
export async function replyQueueKol(agentId: number): Promise<APIResponse<{ count: number }>> {
  return request.post(`/agent/${agentId}/reply/queue-kol`)
}
export async function replyQueueSelf(agentId: number): Promise<APIResponse<{ count: number }>> {
  return request.post(`/agent/${agentId}/reply/queue-self`)
}

export async function checkProjectPromotionUrls(
  urls: string[],
): Promise<APIResponse<{ [key: string]: number }>> {
  return request.post(`/agent/check-project-urls`, { urls })
}

export async function getTargetTopics(): Promise<APIResponse<{ topics: string[] }>> {
  return request.get(`/agent/available-topics`)
}

export async function checkUserExists(usernames: string[]): Promise<APIResponse<string[]>> {
  return request.post('/twitter/check-user-exists', { usernames })
}

export async function checkTransaction(signature: string): Promise<APIResponse<boolean>> {
  return request.post('/twitter/check-transaction', { signature })
}

export async function generateImage(data: {
  lore: string[]
  bio: string
  agentId: number
}): Promise<APIResponse<string[]>> {
  return request.post('/twitter/generate-image', data)
}
export async function generateTwitterProfile(agentId: number): Promise<APIResponse<string[]>> {
  return request.post('/twitter/generate-twitter-profile', { agentId })
}
export async function generateTwitterScreenName(
  agentId: number,
): Promise<APIResponse<{ screenNames: string[] }>> {
  return request.post('/twitter/generate-twitter-profile-screen-name', { agentId })
}
export async function generateTwitterName(
  agentId: number,
): Promise<APIResponse<{ twitterNames: string[] }>> {
  return request.post('/twitter/generate-twitter-profile-name', { agentId })
}
export async function generateTwitterDescription(
  agentId: number,
): Promise<APIResponse<{ description: string }>> {
  return request.post('/twitter/generate-twitter-profile-description', { agentId })
}

export async function getTwitterProfile(
  agentId: number,
): Promise<APIResponse<ts.TwitterProfileInfo>> {
  return request.get(`/twitter/twitter-profile`, { params: { agentId } })
}

export async function updateTwitterProfile(
  data: Partial<ts.TwitterProfileInfo>,
): Promise<APIResponse<void>> {
  return request.post('/twitter/update-twitter-profile', data)
}

export async function generateTwitterImage(data: {
  lore: string[]
  bio: string
  agentId: number
}): Promise<APIResponse<{ bgImage: string[] }>> {
  return request.post('/twitter/generate-twitter-profile-image', data)
}
export async function getImageGenerationHistory(
  agentId: number,
): Promise<APIResponse<{ id: number; imageUrl: string }[]>> {
  return request.get(`/twitter/get-images`, { params: { agentId } })
}

export async function selfReply(params: {
  page?: number
  agentId: number
}): Promise<APIResponse<{ data: ts.Post[]; total: number }>> {
  return request.get(`/twitter/self-reply`, { params })
}

export async function kolReply(params: {
  page?: number
  agentId: number
}): Promise<APIResponse<{ data: ts.Post[]; total: number }>> {
  return request.get(`/twitter/kol-reply`, { params })
}

export async function myPost(params: {
  page?: number
  agentId: number
}): Promise<APIResponse<{ data: ts.Post[]; total: number }>> {
  return request.get(`/twitter/my-post`, { params })
}

export async function manualPostHistory(params: {
  page?: number
  agentId: number
}): Promise<APIResponse<{ data: ts.Post[]; total: number }>> {
  return request.get(`/manual-post/history-manual-post`, { params })
}

export async function getProjectContent(url: string): Promise<APIResponse<string>> {
  return request.post(`agent/preview-project-url`, { url })
}

export async function cloneAgent(agentId: number): Promise<APIResponse<ts.AgentInfo>> {
  return request.post(`agent/${agentId}/clone`)
}

export async function getBindUrl(agentId: number, type: string): Promise<APIResponse<string>> {
  return request.get(`/manual-post/bind-url?agentId=${agentId}&type=${type}`)
}

export async function getTelegramLink(
  agentId: number,
): Promise<APIResponse<{ link: string; command: string }>> {
  return request.get(`/telegram-bot/link/${agentId}`)
}
